// Domain related to simple orders and general order types. Will be extended by particular order types
import { SecurityInfo, SymbolType } from '../../../domain/symbol.domain';
import { Currency, MarketType } from '../../../domain/market.domain';
import { AccountType, TradingPlatform, AccountPlatform, TradingAccount, AccountDetailsResponse } from '../../../domain/account.domain';
import { FormAction } from '../order-entry/domain/order-entry.domain';
import {
  EquityOrderAction,
  GroupOrderStatusType,
  InsiderType,
  LifeCycleType,
  OptionOrderAction,
  OrderStatusType,
  PricePlan,
  PrincipalPriceType,
  Strategy
} from './order.enum';
import { MutualFundsExtendedTerms, MutualFundsSecurityInfo } from './mutual-funds.domain';
import { EquityExtendedTerms, EquityOrderItem } from './equity.domain';
import { OptionExtendedTerms, OptionOrderItem } from './option.domain';
import { Conditions, OrderConditions } from './conditions.domain';
import { MultiLegExtendedTerms, LegExtendedTerms } from './complex.domain';
import { GicExtendedTerms } from './gic.domain';

// TODO REMOVE THIS - DUPLICATE with OrderRequest
export interface Order<T extends  OrderTerms = OrderTerms> {
  conditionFilter: string;
  orderTerms: T;
  acknowledgements?: {
    conditions: Conditions;
  };
  tradingCredential?: {
    tradingPassword: string;
    rememberPassword?: boolean;
  };
}

/**
 * OrderRequest
 * The server-side model object to request on captureRequest (step1)
 */
export interface OrderRequest<T = OrderTermsRequest> {
  conditionFilter: string;
  orderTerms: T;
  acknowledgements?: {
    conditions: Conditions;
  };
  tradingCredential?: TradingCredential;
}

export interface OrderCapture<T extends OrderTerms = OrderTerms> {
  conditions: OrderConditions;
  orderTerms: T;
}

export interface OrderParameters {
  orderType: string;
  actionType: string;
  orderKey: string;
  operation: string;
  action: string;
  symbol: string;
  market: string;
  accountId: string;
  quantity: string;
  optionNonStandard: string;
  securityType: string;
  price: string;
  orderView: boolean;
  reverse: boolean;
  simplifiedOrder: boolean;
}

export interface OrderTerms {
  accountInfo: OrderAccountInfo;
  afterMarket: boolean;
  createdById?: string;
  order: Array<EquityOrderItem | OptionOrderItem | OrderItem>;
  orderToken?: OrderToken;
  pricePlan: PricePlan;
  strategy: Strategy;
  orderSubmissionDate?: Date;
  multilegEntry?: { extendedTerms: MultiLegExtendedTerms };
}

export interface OrderPreferences {
  optionType: string;
  quantity: number;
  quantityIncrement: number;
  stockType: string;
  contract: number;
  contractsIncrement: number;
}

export interface OrderAccountInfo {
  accountId: string;
  accountNumber: string;
  acctType: AccountType;
  currency: Currency;
  accountPlatform: AccountPlatform;
  orderBuyingPower: number;
  tradingPlatform: TradingPlatform;
}

export interface OrderToken {
  orderKeyEncoded: string;
}

export interface WithOrderActionFlags {
  canCancel?: boolean;
  canChange?: boolean;
  canCopy?: boolean;
  canReverse?: boolean;
}

export interface WithOrderStatus {
  orderStatus?: OrderStatusType;
}

export interface InsiderInfo {
  insiderInfo: InsiderType;
  solicited: boolean;
}
// TODO: should be split into two types - one for order request and another for existing orders
export interface OrderItem<T extends SecurityExtendedTerms = SecurityExtendedTerms> extends WithOrderActionFlags, WithOrderStatus {
  clientInfo?: InsiderInfo;
  extendedTerms?: T;
  security?: T extends MutualFundsExtendedTerms ? MutualFundsSecurityInfo : SecurityInfo;
  details?: OrderDetail; // TODO: used in order status / change/ cancel
  grossFilledAmount?: number;
  id?: string;
  lifeCycle?: LifeCycleType; // TODO: not used on the ui
  orderEstimate?: OrderEstimate;
  omsOrderNumber?: string;
  orderNumber?: string;
  orderGroupStatus?: GroupOrderStatusType;
  orderStatus?: OrderStatusType;
  percentFilled?: number;
  quantity: number;
  dollarAmt?: number;
  legChanged?: boolean;
  slipOrderDetail?: SlipOrderDetail;
}

export interface SlipOrderDetail {
  slipSell: boolean;
}

export interface OrderDetail {
  // agentFederatedId?: string;
  agentTraderRRCode?: string;
  // feeOverride?: string;
  fxRate?: number;
  lastUpdatedById?: string;
  lastUpdatedTimestamp?: Date;
  latestPrice?: number;
  latestRequestedQuantity: number;
  marketExchange?: string;
  orderCurrency?: Currency;
  totalFilledQuantity?: number;
  totalFilledDollarAmt?: number;
}

export interface OrderEstimate {
  commission: number;
  currencySymbol: Currency;
  flatAmount: number;
  perShareAmount: number;
  principalPriceType: PrincipalPriceType;
  principalValue: number;
  scheduleCodeType: string;
  schedulePercent: number;
  scheduleType: string;
  total: number;
}

export interface BaseExtendedTerms {
  goodTill?: OrderGoodTil;
  goodTillDate?: Date;
}

/**
 * These are extended terms that are extended by equities and options.
 * Mutual Funds uses its own structure
 **/
export interface OrderExtendedTerms extends BaseExtendedTerms {
  allOrNone?: boolean;
  averageFillPrice?: number;
  executionPriority?: string;
  floorInstruction?: unknown; // leaving this as any because I've never seen us use it anywhere on the frontend
  multipleFillIndicator?: boolean;
  optionInstruction?: string;
  orderFill?: Array<OrderFill>;
  remainingQuantity?: number;
  remainingDollarAmt?: number;
  stopIndicator?: boolean;
  limitPrice?: number;
  triggerPrice?: number;
}

export interface OrderFill {
  fillDate: Date;
  marketExchange?: string;
  price: number;
  quantity: number;
}

export interface OrderPriceAmount {
  priceLimit: number | undefined;
  priceTrigger: number | undefined;
  deltaLimit?: number | undefined;
  deltaTrigger?: number | undefined;
  deltaPercent?: number | undefined;
}

export interface PriceEstimate {
  deltaEstimate: number;
  deltaLimitEstimate: number;
}

export type SecurityExtendedTerms = EquityExtendedTerms | OptionExtendedTerms |
MutualFundsExtendedTerms | LegExtendedTerms | GicExtendedTerms;

export type PriceSelection =
  'MARKET'
  | 'LIMIT'
  | 'OPTION_LIMIT'
  | 'STOP_MARKET'
  | 'STOP_LIMIT'
  | 'TRAILING_STOP_MARKET'
  | 'TRAILING_STOP_LIMIT'
  | 'EVEN'
  | 'NET_DEBIT'
  | 'NET_CREDIT';

export enum FractionalType {
  WHOLESHARE = 'WHOLESHARE',
  FRACTIONAL = 'FRACTIONAL',
  NOTIONAL = 'NOTIONAL'
}

export interface PriceInputDisplay {
  priceLimit: boolean;
  priceTrigger: boolean;
  deltaLimit: boolean;
  deltaTrigger: boolean;
  deltaPercent: boolean;
}

export type PriceSelectDisplay = {
  [priceSelection in Exclude<PriceSelection, 'EVEN' | 'NET_DEBIT' | 'NET_CREDIT'>]: PriceInputDisplay;
};

export interface GoodTilDisplay {
  showDay: boolean;
  showSpecify: boolean;
  showCancel: boolean;
  showEXTMarkets: boolean;
}

export type OrderGoodTil =
  'DAY'
  | 'CANCEL'
  | 'DATE'
  | 'EXTENDED_MARKET';

export type PriceSelectionToGoodTilDisplay = {
  [priceSelection in PriceSelection]: GoodTilDisplay;
};

// TODO REMOVE THIS OR OrderTerms - DUPLICATE with OrderTerms@88
export interface OrderTermsRequest {
  accountInfo: { accountId: string };
  afterMarket: boolean;
  createdById?: string;
  order: Array<OrderItem>;
  orderToken?: OrderToken;
  pricePlan: PricePlan;
  strategy: Strategy;
  orderSubmissionDate?: Date;
  multilegEntry?: { extendedTerms: MultiLegExtendedTerms }; // TODO check need for refactor
}

export interface CancelOrderRequest {
  operation: 'RAPID';
  tradingCredential?: TradingCredential;
}

export interface CancelOrderParameters {
  orderKey: string;
  cancelOrderRequest: CancelOrderRequest;
}

export interface TradingCredential {
  tradingPassword: string;
  rememberPassword?: boolean;
}

export interface EquityOptionsCommonModel {
  accountId?: string; // TODO: this is temporary
  orderPriceAmount: OrderPriceAmount; // are they all optional?
  shareholderStatus: InsiderType;
  orderQuantity: number;
  orderAllOrNone: boolean;
  orderPrice: PriceSelection;  // TODO FIX and use security specific enums
  orderGoodTil: OrderGoodTil;
  orderGoodTilDate?: Date;
  selectedSymbol: SecurityInfo;
  orderAction: EquityOrderAction | OptionOrderAction;
  nonStandard?: boolean;
}

export interface TradingAccountDetails {
  account: TradingAccount;
  accountDetails: AccountDetailsResponse;
}

export interface TradingAction {
  formAction: FormAction;
  orderCaptureResponse?: OrderCapture<OrderTerms>;
  tradingCredential?: TradingCredential;
}

// used on cancel form
export interface CancelSymbol {
  symbol: string;
  market: MarketType;
  type: SymbolType;
}

